import React, { useEffect, useState } from "react";
import { playGameSound } from "../services/gameSounds";
import { useParams, useNavigate } from "react-router-dom";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  getDocs,
  collection,
  query,
  where,
  increment,
  arrayUnion,
  addDoc,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { resolveEducatorUid } from "../utils/resolveEducatorUid";

import "./UniversalQuizGame.css";
// 🪙 Coins: the Final Score screen pays the student (functions/coins.js).
// Additive only — the score, progress and leaderboard saves are untouched.
import CornerCoins from "../components/coins/CornerCoins";
import { GameExitProvider } from "../components/quizzes/gameExit";
import { GameDone, RoundDone } from "../components/quizzes/kit/Celebration";
import CoinReward from "../components/coins/CoinReward";

import MultipleChoice from "../components/quizzes/MultipleChoice";
import DragDropQuiz from "../components/quizzes/DragDropQuiz";
import MatchingQuiz from "../components/quizzes/MatchingQuiz";
import MatchThreeGame from "../components/quizzes/MatchThreeGame";
import BonusRouter from "../components/quizzes/BonusRouter";
import SortingConveyor from "../components/quizzes/SortingConveyor";
import CountPop from "../components/quizzes/CountPop";
import TalkChoice from "../components/quizzes/TalkChoice";
import UniversalMathGame from "../components/quizzes/UniversalMathGame";
import MemoryMatchGame from "../components/quizzes/MemoryMatchGame";

import { stateTopicsMap } from "../data/stateTopicsMap";
import { getQuizBySlug } from "../data/quiz-library";
import { getPortalLayout, makeLayoutId } from "../services/layoutService";

const parseMathGameType = (gameType = "") => {
  const raw = String(gameType || "");

  if (!raw.includes(":")) return null;

  const [modeRaw, valueRaw] = raw.split(":");
  const mode = String(modeRaw || "").toLowerCase();
  const value = Number(valueRaw) || 5;

  if (
    ["mathtimestable", "mathaddition", "mathsubtraction"].includes(mode)
  ) {
    return { mode, value };
  }

  return null;
};

// Content files disagree about how to spell a game type. The topics files say
// "dragAndDrop", the quiz content files say "dragDrop", and the switch below
// only ever matched the second one — so every topic authored with the first
// spelling fell through to "Unsupported quiz type".
const GAME_TYPE_ALIASES = {
  draganddrop: "dragdrop",
  dragndrop: "dragdrop",
  drag: "dragdrop",
  mc: "multiplechoice",
  multiplechoicequiz: "multiplechoice",
  matchingquiz: "matching",
  memory: "memorymatch",
  memorymatchgame: "memorymatch",
  matchthreegame: "match3",
  countandpop: "countpop",
};

const normalizeGameType = (value = "") => {
  const base = String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

  return GAME_TYPE_ALIASES[base] || base;
};

// Slugs drift between the topics files and the quiz content files -
// "africa-features" vs "africafeatures", for one. Comparing on a stripped
// form lets the question lookup survive that without a mass rename.
const normalizeSlug = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const findBySlug = (list = [], slug) => {
  const target = normalizeSlug(slug);

  return (
    list.find((item) => item?.slug === slug) ||
    list.find((item) => normalizeSlug(item?.slug) === target) ||
    null
  );
};

// A topic can declare one game type while the questions it actually resolves
// to are shaped for another. MultipleChoice reads `current.answers` without
// guarding, so pointing it at drag-drop data throws and the player sees a
// blank screen. Read the shape and trust it over the label.
const inferGameTypeFromQuestions = (questions = []) => {
  const first = questions.find(Boolean);

  if (!first) return null;

  if (Array.isArray(first.answers) && first.answers.length) {
    return "multipleChoice";
  }

  if (
    Array.isArray(first.options) &&
    first.options.some((option) => option?.targetText || option?.targetImage)
  ) {
    return "dragDrop";
  }

  return null;
};

const SUPPORTED_GAME_TYPES = new Set([
  "multiplechoice",
  "dragdrop",
  "matching",
  "matchquiz",
  "match3",
  "matchthree",
  "countpop",
  "talkchoice",
  "memorymatch",
]);

const reconcileGameType = (topic) => {
  const questions = topic?.questions || [];

  if (!questions.length) return topic;

  // Math games carry their config in the string ("mathTimesTable:5"), so the
  // shape check doesn't apply to them.
  if (parseMathGameType(topic.gameType)) return topic;

  const inferred = inferGameTypeFromQuestions(questions);

  if (!inferred) return topic;

  const declared = normalizeGameType(topic.gameType);

  if (declared === normalizeGameType(inferred)) return topic;

  const wouldCrash =
    declared === "multiplechoice" && !Array.isArray(questions[0]?.answers);

  const unsupported = !SUPPORTED_GAME_TYPES.has(declared);

  if (wouldCrash || unsupported) {
    console.warn(
      `[UniversalQuizGame] "${topic.slug}" declares gameType "${topic.gameType}" but its questions are shaped for "${inferred}". Using "${inferred}".`
    );

    return { ...topic, gameType: inferred };
  }

  return topic;
};

const shuffle = (array = []) =>
  [...array]
    .map((q) => ({
      ...q,
      answers: Array.isArray(q.answers)
        ? [...q.answers].sort(() => Math.random() - 0.5)
        : q.answers,
      options: Array.isArray(q.options)
        ? [...q.options].sort(() => Math.random() - 0.5)
        : q.options,
    }))
    .sort(() => Math.random() - 0.5);

const normalizeGrade = (grade = "1st") => {
  return String(grade).split(",")[0].trim().toLowerCase();
};

const normalizePayload = (payload) => {
  if (Array.isArray(payload?.answers)) return payload;

  const details = payload?.details || [];
  const answers = details.flatMap((round) =>
    (round?.pairs || []).map((p) => ({
      question: round?.question
        ? `${round.question} — ${p.targetLabel}`
        : `Match: ${p.targetLabel}`,
      selectedText: p.studentLabel || "(no answer)",
      correctText: p.correctLabel || "(unknown)",
      correct: !!p.isCorrect,
    }))
  );

  return { answers, details };
};

const mergedTopicsMapBySubject = Object.values(stateTopicsMap).reduce(
  (acc, cur) => ({
    ...acc,
    ...cur,
  }),
  {}
);

const UniversalQuizGame = () => {
  const params = useParams();

  const slug = params.slug;
  const subject = params.subject;
  const routeState = params.state;
  const routeGrade = params.grade;
  const routeView = params.view;
  const navigate = useNavigate();

  const db = getFirestore();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [topic, setTopic] = useState(null);
  const [questions, setQuestions] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadMessage, setLoadMessage] = useState("");

  const [quizScore, setQuizScore] = useState(0);
  const [bonusScore, setBonusScore] = useState(null);
  // 🏁 the bonus round was ended early: the points count, but the game is not
  // marked finished (no ⭐ star, the treasure chest stays shut)
  const [bonusEndedEarly, setBonusEndedEarly] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [showFinalResults, setShowFinalResults] = useState(false);
  const [answersDetail, setAnswersDetail] = useState([]);

  const [returnPath, setReturnPath] = useState("/dashboard");
  const [bonusReady, setBonusReady] = useState(false); // past the "questions done!" card

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setLoading(false);
        setLoadMessage("Please log in to play this game.");
        return;
      }

      try {
        setLoading(true);

        const userSnap = await getDoc(doc(db, "users", currentUser.uid));

        if (!userSnap.exists()) {
          setLoading(false);
          setLoadMessage("User profile not found.");
          return;
        }

        const userData = {
          uid: currentUser.uid,
          ...userSnap.data(),
        };

        setUser(userData);

        const state = String(routeState || userData.state || "nc").toLowerCase();
        const grade = normalizeGrade(routeGrade || userData.grade || "1st");
        // trial teachers are role "unpaid" until they pay — check intendedRole too
        const role = `${userData.role || ""} ${userData.intendedRole || ""}`.toLowerCase();
        
        const preferredView =
          routeView ||
          (role.includes("educator") ? "educator" : "student");

        // opened from the treasure map? go back to the map (same spot)
        const fromMap = new URLSearchParams(window.location.search).get("from") === "map";
        setReturnPath(fromMap ? `/dynamic-treasure-map/${state}/${grade}/${subject}/${preferredView}/${params.slug || ""}` : `/portal/${state}/${grade}/${subject}/${preferredView}`);

        const loadedTopic = await loadTopicForGame({
          state,
          grade,
          subject,
          slug,
          preferredView,
        });

        if (!loadedTopic) {
          setTopic(null);
          setQuestions([]);
          setLoadMessage(`Topic not found for: ${slug}`);
          setLoading(false);
          return;
        }

        const hydratedTopic = await hydrateTopicQuestions(loadedTopic);

        setTopic(hydratedTopic);
        setQuestions(shuffle(hydratedTopic.questions || []));
        setLoadMessage("");
      } catch (error) {
        console.error("Error loading universal quiz game:", error);
        setLoadMessage("Error loading game. Check console.");
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [auth, db, slug, subject]);

  const loadTopicForGame = async ({
    state,
    grade,
    subject,
    slug,
    preferredView,
  }) => {
    const possibleLayoutIds = [
      makeLayoutId({ state, grade, subject, view: preferredView }),
      makeLayoutId({ state, grade, subject, view: "student" }),
      makeLayoutId({ state, grade, subject, view: "educator" }),
    ];

    for (const layoutId of possibleLayoutIds) {
      const layout = await getPortalLayout(layoutId);

      if (!layout?.topics?.length) continue;

      const found = findBySlug(layout.topics, slug);

      if (found) {
        return {
          ...found,
          layoutId,
          state,
          grade,
          subject,
          source: "portalLayouts",
        };
      }
    }

    const fallbackKey = `${state}_${grade}_${subject}`;
    const fallbackTopics = mergedTopicsMapBySubject[fallbackKey] || [];
    const fallbackTopic = findBySlug(fallbackTopics, slug);

    if (fallbackTopic) {
      return {
        ...fallbackTopic,
        state,
        grade,
        subject,
        source: "stateTopicsMap",
      };
    }

    return null;
  };

  const hydrateTopicQuestions = async (baseTopic) => {
    if (Array.isArray(baseTopic.questions) && baseTopic.questions.length > 0) {
      return reconcileGameType(baseTopic);
    }

    // -------------------------------------------------------------------
    // EDUCATOR-AUTHORED QUESTIONS — highest priority after inline.
    // -------------------------------------------------------------------
    // A teacher who wrote their own questions for this topic should see
    // them, and nobody else's class should change. That's why the path is
    // scoped by educatorCode rather than reusing `quizzes/{slug}`, which is
    // the shared global library with no owner in the path — one teacher
    // writing there would overwrite that topic for every school.
    // See src/pages/educator/EducatorQuizEditor.js.
    if (user?.educatorCode && baseTopic.slug) {
      try {
        const eduSnap = await getDoc(
          doc(
            db,
            "educatorQuizzes",
            String(user.educatorCode),
            "topics",
            baseTopic.slug
          )
        );

        if (eduSnap.exists()) {
          const eduQuiz = eduSnap.data();
          if (Array.isArray(eduQuiz?.questions) && eduQuiz.questions.length) {
            return reconcileGameType({
              ...baseTopic,
              ...eduQuiz,
              title: baseTopic.title || eduQuiz.title,
              slug: baseTopic.slug,
              gameType: eduQuiz.gameType || baseTopic.gameType,
              questions: eduQuiz.questions,
              source: "educatorQuizzes",
            });
          }
        }
      } catch (err) {
        // A student whose rules don't permit this read just falls through to
        // the standard library — never a blocked game.
        console.warn(
          "[UniversalQuizGame] educator quiz lookup skipped:",
          err?.code || err?.message
        );
      }
    }

    // An explicit quizId is an override, so it's tried next.
    if (baseTopic.quizId) {
      const quizTopic = await loadQuizDoc(baseTopic.quizId);

      if (quizTopic?.questions?.length) {
        return reconcileGameType({
          ...baseTopic,
          ...quizTopic,
          title: baseTopic.title || quizTopic.title,
          slug: baseTopic.slug || quizTopic.slug,
          gameType: quizTopic.gameType || baseTopic.gameType,
          questions: quizTopic.questions,
        });
      }
    }

    // The real quiz library. QuizUploader and QuizManager both write
    // setDoc(doc(db, "quizzes", slug)) — the document ID *is* the topic slug.
    // Nothing read it back: loadQuizDoc only ran when a topic carried a
    // quizId, and seeded topics don't (emptyTopic sets quizId: ""). So every
    // quiz in Firestore was unreachable and the game fell through to the
    // static per-state files instead, which is why the wrong questions showed
    // up on the topics that rendered at all.
    if (baseTopic.slug) {
      const bySlug =
        (await loadQuizDoc(baseTopic.slug)) ||
        (await loadQuizDoc(normalizeSlug(baseTopic.slug)));

      if (bySlug?.questions?.length) {
        return reconcileGameType({
          ...baseTopic,
          ...bySlug,
          title: baseTopic.title || bySlug.title,
          slug: baseTopic.slug,
          gameType: bySlug.gameType || baseTopic.gameType,
          questions: bySlug.questions,
        });
      }
    }

    // -------------------------------------------------------------------
    // SHARED QUIZ LIBRARY — state-agnostic, keyed by slug.
    // -------------------------------------------------------------------
    // This is why games only worked in Virginia.
    //
    // 227 of the 245 *QuizContent.js files in src/data are a copy of the same
    // nineteen-topic FIRST GRADE SCIENCE set. ca5thSocialStudiesQuizContent.js
    // contains first grade science. So does nc3rdSocialStudies, and dc4thMath.
    // Only VA and MD held real questions, and those two are byte-identical.
    //
    // The per-state fallback below looks up `state_grade_subject`, so a
    // California student got routed into the California file, found first
    // grade science, and saw the wrong questions or a blank screen. Virginia
    // worked because Virginia is where the questions actually were.
    //
    // A quiz is not state-specific — "Ancient Egypt" is the same five
    // questions in Sacramento and Richmond. src/data/quiz-library holds all
    // 209 real ones keyed by slug, so any state whose topic carries that slug
    // gets the game. This runs BEFORE the per-state files, which means a
    // correct quiz always beats a misfiled one.
    if (baseTopic.slug) {
      const shared = getQuizBySlug(baseTopic.slug);

      if (shared?.questions?.length) {
        return reconcileGameType({
          ...baseTopic,
          ...shared,
          title: baseTopic.title || shared.title,
          slug: baseTopic.slug,
          gameType: shared.gameType || baseTopic.gameType,
          questions: shared.questions,
          source: "quizLibrary",
        });
      }
    }

    // Last resort only. These files are per-state and known to be unreliable —
    // see above. Kept solely so a state that genuinely has its own authored
    // file still resolves; Firestore and the shared library both beat it.
    const fallbackKey = `${baseTopic.state}_${baseTopic.grade}_${baseTopic.subject}`;
    const fallbackTopics = mergedTopicsMapBySubject[fallbackKey] || [];
    const fallbackTopic = findBySlug(fallbackTopics, baseTopic.slug);

    if (fallbackTopic?.questions?.length) {
      return reconcileGameType({
        ...fallbackTopic,
        ...baseTopic,
        questions: fallbackTopic.questions,
        gameType: baseTopic.gameType || fallbackTopic.gameType,
      });
    }

    return {
      ...baseTopic,
      questions: [],
    };
  };

  // Named for what it does: fetch a quiz document by ID. The ID is a topic
  // slug when it comes from the quizzes collection.
  const loadQuizDoc = async (docId) => {
    if (!docId) return null;

    const possibleCollections = ["quizzes", "portalQuizzes", "quizContent"];

    for (const collectionName of possibleCollections) {
      try {
        const snap = await getDoc(doc(db, collectionName, docId));

        if (snap.exists()) {
          return {
            id: snap.id,
            ...snap.data(),
          };
        }
      } catch (error) {
        // A collection the rules don't allow, or that doesn't exist,
        // shouldn't stop the remaining lookups.
        console.warn(
          `[UniversalQuizGame] could not read ${collectionName}/${docId}:`,
          error?.message || error
        );
      }
    }

    return null;
  };

  const handleQuizComplete = (payload = {}, legacyDetails = []) => {
    if (typeof payload === "number") {
      setQuizScore(payload);
      setAnswersDetail(legacyDetails || []);
      setQuizComplete(true);
      return;
    }
  
    const normalized = normalizePayload(payload);
    const score = Number(payload.score ?? 0) || 0;
  
    setQuizScore(score);
    setAnswersDetail(normalized.answers || []);
    setQuizComplete(true);
  };

  const handleBonusComplete = async (score, meta) => {
    setBonusEndedEarly(Boolean(meta?.endedEarly));
    setBonusScore(Number(score) || 0);
    setShowFinalResults(true);
  };

  const saveToLeaderboard = async ({ totalScore, quizScore, bonusScore }) => {
    if (!user || !slug) return;

    try {
      const playerName =
        `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
        user.firstName ||
        "Anonymous";

      await addDoc(collection(db, `leaderBoard_${slug}`), {
        playerId: playerName,
        uid: user.uid,
        avatar: user.avatar || "",
        quizScore,
        bonusRoundScore: bonusScore,
        totalScore,
        subject,
        slug,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error("Failed to save leaderboard score:", error);
    }
  };

  const saveGameProgress = async ({ totalScore, quizScore, bonusScore }) => {
    if (!user?.uid || !topic?.slug) return;

    const state = String(user.state || topic.state || "nc").toLowerCase();
    const grade = normalizeGrade(user.grade || topic.grade || "1st");
    const subjectKey = `${state}_${grade}_${subject}`;

    const patch = {
      game: {
        // An early-ended bonus round keeps its scores but doesn't finish the
        // game. `completed` is left out (not set false) so a game finished on
        // an earlier play stays finished.
        ...(bonusEndedEarly ? {} : { completed: true, timestamp: new Date().toISOString() }),
        quizScore,
        latestBonus: bonusScore,
        latestTotal: totalScore,
        latestDetail: {
          at: new Date().toISOString(),
          answers: answersDetail,
        },
        score: arrayUnion(totalScore),
        quizPlays: increment(1),
        bonusPlays: increment(1),
      },
    };

    await setDoc(
      doc(db, "userData", user.uid),
      {
        [subjectKey]: {
          [topic.slug]: patch,
        },
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        sirName: user.sirName || "",
        state,
        grade,
        educatorCode: user.educatorCode || "",
      },
      { merge: true }
    );

    await mirrorProgressToEducator({
      subjectKey,
      patch,
    });
  };

  // Was a where("role","in",[...]) query against /users built from guessed
  // role spellings. One directory GET instead — utils/resolveEducatorUid.js.
  const mirrorProgressToEducator = async ({ subjectKey, patch }) => {
    if (!user?.educatorCode) return;

    try {
      const educatorUid = await resolveEducatorUid(db, user.educatorCode);
      if (!educatorUid) return;

      await setDoc(
        doc(
          db,
          "classroomProgress",
          educatorUid,
          "subjects",
          subjectKey,
          "students",
          user.uid
        ),
        {
          studentUid: user.uid,
          studentName:
            `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
            user.firstName ||
            "Student",
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          avatarUrl: user.avatarUrl || "",
          educatorCode: user.educatorCode,
          slugs: {
            [topic.slug]: patch,
          },
          lastUpdated: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (error) {
      console.error("Error mirroring progress to educator:", error);
    }
  };

  const handleContinue = async () => {
    try {
      const finalBonusScore = bonusScore ?? 0;
      const totalScore = quizScore + finalBonusScore;

      await saveGameProgress({
        totalScore,
        quizScore,
        bonusScore: finalBonusScore,
      });

      await saveToLeaderboard({
        totalScore,
        quizScore,
        bonusScore: finalBonusScore,
      });

      navigate(returnPath);
    } catch (error) {
      console.error("Error finalizing game results:", error);
      navigate(returnPath); // don't trap the child on this screen
    }
  };

  const renderQuizContent = () => {
    const mathGame = parseMathGameType(topic?.gameType);
  
    if (mathGame) {
      return (
        <UniversalMathGame
          mode={mathGame.mode}
          value={mathGame.value}
          questions={questions}
          onComplete={handleQuizComplete}
          user={user}
          topic={topic}
          placeholderImage={topic.placeholderImage}
        />
      );
    }
  
    const gameType = normalizeGameType(topic?.gameType);
  
    switch (gameType) {
      case "multiplechoice":
        return (
          <MultipleChoice
            questions={questions}
            onComplete={handleQuizComplete}
            user={user}
            topic={topic}
          />
        );

      // Same data as dragdrop — options with text + targetText — but sorted by
      // tapping rather than dragging. Any topic can be switched between the
      // two by changing gameType alone, with no content change.
      case "sortingconveyor":
      case "conveyor":
        return (
          <SortingConveyor
            questions={questions}
            onComplete={handleQuizComplete}
            user={user}
            topic={topic}
            placeholderImage={topic.placeholderImage}
          />
        );

      case "dragdrop":
        return (
          <DragDropQuiz
            questions={questions}
            onComplete={handleQuizComplete}
            user={user}
            topic={topic}
            placeholderImage={topic.placeholderImage}
            placeholderStyle={topic.placeholderStyle}
          />
        );

      case "matching":
      case "matchquiz":
        return (
          <MatchingQuiz
            questions={questions}
            onComplete={handleQuizComplete}
            user={user}
            topic={topic}
          />
        );

      case "match3":
      case "matchthree":
        return (
          <MatchThreeGame
            questions={questions}
            onComplete={handleQuizComplete}
            user={user}
            topic={topic}
          />
        );

      case "countpop":
        return (
          <CountPop
            topic={topic}
            questions={questions}
            onComplete={({ score, details }) => {
              setQuizScore(Number(score) || 0);
              setAnswersDetail(details || []);
              setQuizComplete(true);
            }}
          />
        );

        case "talkchoice":
          return (
            <TalkChoice
              questions={questions}
              topic={topic}
              onComplete={(res) => {
                const score =
                  typeof res === "number" ? res : Number(res?.score || 0);
                const details = res?.details || [];
                setQuizScore(score);
                setAnswersDetail(details);
                setQuizComplete(true);
              }}
            />
          );
        
        case "memorymatch":
          return (
            <MemoryMatchGame
              questions={questions}
              onComplete={handleQuizComplete}
              user={user}
              topic={topic}
              placeholderImage={topic.placeholderImage}
            />
          );
        
        default:
          return <p>❌ Unsupported quiz type: {topic?.gameType}</p>;
    }
  };

  // 🎵 Game sounds (Layout Builder): "Questions finished — good job!" when the
  // questions are done, "Game over" on the finish screen. Silent unless an
  // admin uploaded one, so nothing changes until then.
  useEffect(() => {
    if (quizComplete) playGameSound("roundDone", "");
  }, [quizComplete]);
  useEffect(() => {
    if (showFinalResults) playGameSound("gameOver", "");
  }, [showFinalResults]);

  if (loading) {
    return <div className="universal-quiz-container">Loading game...</div>;
  }

  if (!topic) {
    return (
      <div className="universal-quiz-container">
        <h2>Topic not found</h2>
        <p>{loadMessage}</p>
        <button onClick={() => navigate(returnPath)}>Return to Portal</button>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="universal-quiz-container">
        <h2>No questions found for this topic yet.</h2>
        <p>
          Topic: <strong>{topic.title || topic.slug}</strong>
        </p>
        <p>
          Add questions directly to the topic in Firestore, or set a quizId that
          points to a document in <strong>portalQuizzes</strong>,{" "}
          <strong>quizzes</strong>, or <strong>quizContent</strong>.
        </p>
        <button onClick={() => navigate(returnPath)}>Return to Portal</button>
      </div>
    );
  }

  if (!quizComplete) {
    // GameExitProvider: every game's Return button goes back to the subject
    // page this game was opened from (components/quizzes/gameExit.js)
    return (
      <GameExitProvider returnPath={returnPath}>
        <CornerCoins />
        {renderQuizContent()}
      </GameExitProvider>
    );
  }

  const rightCount = Array.isArray(answersDetail) ? answersDetail.filter((a) => a?.correct).length : 0;
  const answerCount = Array.isArray(answersDetail) ? answersDetail.length : 0;

  // "Questions done!" — stars and a cheer before the bonus round
  if (quizComplete && bonusScore === null && !bonusReady) {
    return (
      <>
        <CornerCoins />
        <RoundDone right={rightCount} total={answerCount} onGo={() => setBonusReady(true)} />
      </>
    );
  }

  if (quizComplete && bonusScore === null) {
    return (
      <BonusRouter
  slug={topic.slug}
  bonusRoundType={topic.bonusRoundType || "diamondTap"}
  questions={questions}
  placeholderImage={topic.placeholderImage}
  onComplete={handleBonusComplete}
  grade={normalizeGrade(params.grade || user?.grade || topic?.grade || "2nd")}
/>
    );
  }

  // The finish: the player's avatar cheering, stars, scores and coins
  // (no pirate ship here — that background is for the bonus round only)
  if (showFinalResults) {
    return (
      <>
        <CornerCoins />
        <GameDone
          title={topic?.title || ""}
          quizScore={quizScore}
          bonusScore={bonusScore ?? 0}
          right={answerCount ? rightCount : 1}
          total={answerCount || 1}
          onContinue={handleContinue}
        >
          {bonusEndedEarly && (
            <p style={{ margin: "6px auto 10px", maxWidth: 440, padding: "8px 14px", borderRadius: 14, background: "#fff7e0", color: "#7a4b00", fontWeight: 800, textAlign: "center" }}>
              🏁 You ended the bonus round early, so this game&apos;s ⭐ star isn&apos;t earned yet — your points still count! Play it all the way through to get the star.
            </p>
          )}
          <CoinReward
            slug={topic?.slug || slug}
            title={topic?.title || slug}
            quizScore={quizScore}
            bonusScore={bonusScore ?? 0}
            correct={answerCount ? rightCount : null}
          />
        </GameDone>
      </>
    );
  }

  return null;
};

export default UniversalQuizGame;